import { useCallback, useMemo } from "react";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import { View, Autocomplete, Banner, Button, Icon, Input, SingleDatePicker } from "@visioni/a3s-ui/components";
import { COMPILATION_MAX_RANGE_DAYS } from "../../../client-messages/compilation";
import { earliestStartDate, latestEndDate } from "./date-range";
import type { FormField } from "./useRequestCompilationForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RequestCompilationFormProps<TCamera extends object> {
  readonly cameras: readonly TCamera[];
  readonly getCameraLabel: (camera: TCamera) => string;
  readonly getCameraId: (camera: TCamera) => string;
  readonly camera: FormField<string | null>;
  readonly startDate: FormField<Date | null>;
  readonly startTime: FormField<string>;
  readonly endDate: FormField<Date | null>;
  readonly endTime: FormField<string>;
  readonly canSubmit: boolean;
  readonly onSubmit: () => void;
  readonly loading?: boolean | undefined;
  /** Maximum selectable date. */
  readonly maxDate?: Date | undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function RequestCompilationFormInner<TCamera extends object>({
  cameras,
  getCameraLabel,
  getCameraId,
  camera,
  startDate,
  startTime,
  endDate,
  endTime,
  canSubmit,
  onSubmit,
  loading,
  maxDate,
}: RequestCompilationFormProps<TCamera>) {
  const selectedCamera = useMemo(
    () => cameras.find((c) => getCameraId(c) === camera.value) ?? null,
    [cameras, camera.value, getCameraId],
  );

  const handleCameraChange = useCallback(
    (value: TCamera | null) => {
      camera.onChange(value ? getCameraId(value) : null);
    },
    [getCameraId, camera],
  );

  // Keeps the pickers from letting a span wider than the backend's own limit
  // (`RequestCompilationBody`'s refine check in `client-messages/compilation.ts`)
  // be selected in the first place, on top of the overall `maxDate` ceiling.
  // Both bounds account for the entered times — see `date-range.ts` for why a
  // whole-day count alone offered spans the form then rejected.
  const startDateMinDate =
    endDate.value ?
      earliestStartDate({ endDate: endDate.value, startTime: startTime.value, endTime: endTime.value })
    : undefined;
  const endDateMaxDate =
    startDate.value ?
      latestEndDate({ startDate: startDate.value, startTime: startTime.value, endTime: endTime.value, maxDate })
    : maxDate;

  return (
    <View style={styles.container}>
      <Banner
        variant="info"
        icon={<Icon name="info" size={16} />}
        title="Compilation limits"
        message={`Maximum range is ${String(COMPILATION_MAX_RANGE_DAYS)} days per request. Longer selections take more time to process.\nClips are taken directly from the camera, footage may have been overwritten for older recordings.`}
      />

      <View style={[styles.field, { zIndex: 3 }]}>
        <Autocomplete<TCamera>
          options={cameras as TCamera[]}
          value={selectedCamera}
          onChange={handleCameraChange}
          getOptionLabel={getCameraLabel}
          getOptionId={getCameraId}
          placeholder="Select camera"
          label="Camera"
        />
      </View>

      <View style={[styles.row, { zIndex: 2 }]}>
        <View style={[styles.dateField, { zIndex: 2 }]}>
          <SingleDatePicker
            value={startDate.value}
            onChange={startDate.onChange}
            label="Start date"
            placeholder="Select date"
            minDate={startDateMinDate}
            maxDate={endDate.value ?? maxDate}
            clearable
          />
        </View>
        <View style={styles.timeField}>
          <Input
            value={startTime.value}
            onChangeText={startTime.onChange}
            label="Start time"
            placeholder="HH:MM"
            error={startTime.error}
          />
        </View>
      </View>

      <View style={[styles.row, { zIndex: 1 }]}>
        <View style={[styles.dateField, { zIndex: 1 }]}>
          <SingleDatePicker
            value={endDate.value}
            onChange={endDate.onChange}
            label="End date"
            placeholder="Select date"
            minDate={startDate.value ?? undefined}
            maxDate={endDateMaxDate}
            clearable
          />
        </View>
        <View style={styles.timeField}>
          <Input
            value={endTime.value}
            onChangeText={endTime.onChange}
            label="End time"
            placeholder="HH:MM"
            error={endTime.error}
          />
        </View>
      </View>

      <View style={styles.actions}>
        <Button
          label="Request"
          variant="primary"
          size="md"
          onPress={onSubmit}
          disabled={!canSubmit}
          loading={loading ?? false}
          loadingText="Requesting…"
        />
      </View>
    </View>
  );
}

RequestCompilationFormInner.displayName = "RequestCompilationForm";

const RequestCompilationForm = RequestCompilationFormInner as <TCamera extends object>(
  props: RequestCompilationFormProps<TCamera>,
) => ReturnType<typeof RequestCompilationFormInner>;

export { RequestCompilationForm };

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.xl,
  },
  field: {
    // full width
  },
  row: {
    flexDirection: "row",
    gap: theme.spacing.lg,
    alignItems: "flex-start",
  },
  dateField: {
    flex: 2,
  },
  timeField: {
    flex: 1,
    minWidth: 100,
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingTop: theme.spacing.md,
  },
}));
